/** UF catastrales oficiales y overlay del usuario. */

import { formatSurfaceDisplay } from './surfaceDisplay';

const stripDiacritics = (value) => String(value)
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '');

/**
 * Clave estable de provincia: "Córdoba" → `cordoba`.
 *
 * @param {string|null|undefined} province
 * @returns {string|null}
 */
export function normalizeProvinceKey(province) {
  const raw = stripDiacritics(String(province ?? '').trim()).toLowerCase();
  if (!raw) return null;
  if (raw === 'cba' || raw === 'provincia de cordoba') return 'cordoba';
  return raw.replace(/\s+/g, '_');
}

/** Base del portal inmobiliario de Rentas Córdoba. */
export const RENTAS_INMOBILIARIO_URL =
  'https://www.rentascordoba.gob.ar/emision/ver-y-pagar/inmobiliario';

/**
 * Portales fiscales por provincia. Hoy solo Córdoba; al sumar otra, agregar
 * una entrada aquí — el bloque catastral no cambia.
 *
 * @type {Record<string, { label: string, buildUrl: (taxAccount: string) => string }>}
 */
export const OFFICIAL_TAX_PORTAL_BY_PROVINCE = {
  cordoba: {
    label: 'Rentas',
    buildUrl: (taxAccount) => `${RENTAS_INMOBILIARIO_URL}/${encodeURIComponent(taxAccount)}`,
  },
};

/**
 * Provincias con padrón de UF oficiales filtrable. Hoy solo Córdoba; al sumar
 * otra, agregar la clave aquí (misma que `normalizeProvinceKey`).
 */
export const OFFICIAL_UNIT_FILTER_PROVINCE_KEYS = ['cordoba'];

/**
 * @param {string|null|undefined} province
 * @returns {boolean}
 */
export function provinceHasOfficialUnitFilters(province) {
  const key = normalizeProvinceKey(province);
  return Boolean(key && OFFICIAL_UNIT_FILTER_PROVINCE_KEYS.includes(key));
}

export const OFFICIAL_UNIT_ALREADY_ANNOTATED_ERROR = 'Official unit already annotated';

/**
 * Link al organismo fiscal de la UF oficial. Córdoba → Rentas. Otras provincias
 * se agregan en `OFFICIAL_TAX_PORTAL_BY_PROVINCE`. Sin portal conocido, `null`.
 *
 * Si no hay provincia, se asume Córdoba: hoy es el único origen de UF oficiales.
 *
 * @param {object} [options]
 * @param {string|null|undefined} options.taxAccount
 * @param {string|null|undefined} [options.province] - Nombre de provincia del lote.
 * @returns {{ href: string, label: string }|null}
 */
export function buildOfficialTaxPortalLink({ taxAccount, province } = {}) {
  const account = String(taxAccount ?? '').trim();
  if (!account) return null;

  const key = normalizeProvinceKey(province) || 'cordoba';
  const portal = OFFICIAL_TAX_PORTAL_BY_PROVINCE[key];
  if (!portal) return null;

  return {
    href: portal.buildUrl(account),
    label: `Ver en ${portal.label}`,
  };
}

/**
 * `unit_number` es el vínculo de producto: string tal cual (`"001"`, no `1`).
 *
 * @param {object|null|undefined} unit
 * @returns {string|null}
 */
export function getOfficialUnitNumber(unit) {
  if (unit == null) return null;
  if (unit.unit_number == null) return null;
  const value = String(unit.unit_number).trim();
  return value || null;
}

/**
 * Bloque catastral embebido en lecturas de `user_lots` (`official_unit`).
 *
 * @param {object|null|undefined} source - Unidad de usuario o respuesta de create/detail.
 * @returns {object|null}
 */
export function getOfficialUnitFromUserLot(source) {
  const official = source?.official_unit;
  if (!official || typeof official !== 'object') return null;
  return official;
}

export function isOfficialOverlayUserLot(unit) {
  return getOfficialUnitFromUserLot(unit) != null;
}

/** UF propias extra: `user_lots` sin vínculo a una oficial. */
export function getOwnExtraUserLots(units) {
  return (Array.isArray(units) ? units : []).filter((unit) => !isOfficialOverlayUserLot(unit));
}

/**
 * Espacio ajeno: deja en cada UF catastral solo el `user_lot_id` que sea una UF
 * del owner (`getuserlotunits?owner_user_id=`). Con la API nueva ya vienen del
 * owner; si llegara uno que no está (API vieja con el del invitado, o carga
 * desfasada), se trata como no cargada: nunca se mezcla una UF del invitado.
 * Propio espacio → no llamar (lista tal cual).
 *
 * @param {object[]} officialUnits - `getlotfunctionalunits` (`units`).
 * @param {object[]} ownerUserLots - UF del owner.
 * @returns {object[]}
 */
export function scopeOfficialUnitsToOwnerOverlays(officialUnits, ownerUserLots) {
  const list = Array.isArray(officialUnits) ? officialUnits : [];
  const ownerIds = new Set(
    (Array.isArray(ownerUserLots) ? ownerUserLots : [])
      .map((unit) => String(unit?.id || unit?.user_lot_id || ''))
      .filter(Boolean),
  );
  return list.map((unit) => {
    if (!unit?.user_lot_id || ownerIds.has(String(unit.user_lot_id))) return unit;
    return { ...unit, user_lot_id: null };
  });
}

/**
 * Indexa overlays del usuario por `user_lot_id` para enriquecer la lista catastral
 * (título, precio) sin duplicar la oficial como card propia.
 *
 * @param {object[]} userLots
 * @returns {Map<string, object>}
 */
export function indexUserLotsById(userLots) {
  const map = new Map();
  (Array.isArray(userLots) ? userLots : []).forEach((unit) => {
    const id = unit?.id || unit?.user_lot_id;
    if (!id) return;
    map.set(String(id), unit);
  });
  return map;
}

/**
 * 409 al anotar una oficial que este usuario ya cubrió.
 *
 * @param {object|null|undefined} res - Envelope de `httpClient`.
 * @returns {string|null} `user_lot_id` existente, o null.
 */
export function getAlreadyAnnotatedUserLotId(res) {
  if (!res || res._httpStatus !== 409) return null;
  if (res.user_lot_id) return String(res.user_lot_id);
  if (res.error === OFFICIAL_UNIT_ALREADY_ANNOTATED_ERROR && res.data?.user_lot_id) {
    return String(res.data.user_lot_id);
  }
  return null;
}

/**
 * @param {string|null|undefined} period - Ej. `"202601"`.
 * @returns {string|null} `"01/2026"` si el formato es AAAAMM.
 */
export function formatValuationPeriod(period) {
  const raw = String(period ?? '').trim();
  if (!raw) return null;
  if (/^\d{6}$/.test(raw)) {
    return `${raw.slice(4, 6)}/${raw.slice(0, 4)}`;
  }
  return raw;
}

/**
 * @param {number|string|null|undefined} value
 * @returns {string|null}
 */
export function formatFiscalArs(value) {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return null;
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

/**
 * @param {number|string|null|undefined} valueM2
 * @returns {string|null}
 */
export function formatOfficialBuiltSurface(valueM2) {
  const display = formatSurfaceDisplay(valueM2);
  if (!display) return null;
  return `${display.value} ${display.unit}`;
}

/**
 * Superficie edificada oficial en m². `null` si el padrón no la trae.
 * @param {object|null|undefined} officialUnit
 * @returns {number|null}
 */
export function parseOfficialBuiltSurfaceM2(officialUnit) {
  const n = Number(officialUnit?.built_surface_m2);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Prefija `covered_surface` con el m² catastral. Sin dato, no toca el form.
 * @param {object} form
 * @param {object|null|undefined} officialUnit
 * @returns {object}
 */
export function applyOfficialBuiltSurfaceToForm(form, officialUnit) {
  const surface = parseOfficialBuiltSurfaceM2(officialUnit);
  if (surface == null || !form) return form;
  const next = String(surface);
  if (form.covered_surface === next) return form;
  return { ...form, covered_surface: next };
}

/**
 * Copy del recorte UF: `unit_count` / `match_count` del POST, sin mandar UF al
 * mapa. El caso sin coincidencias lo cubre el estado `empty` del panel.
 *
 * @param {{ unitCount?: number|null, matchCount?: number|null }} [counts]
 * @returns {string|null}
 */
export function formatOfficialUnitFilterCounts({ unitCount, matchCount } = {}) {
  const units = Number(unitCount);
  const lots = Number(matchCount);
  if (!Number.isFinite(units) || !Number.isFinite(lots) || units <= 0 || lots <= 0) return null;
  const unitLabel = units === 1 ? 'unidad' : 'unidades';
  const lotLabel = lots === 1 ? 'lote' : 'lotes';
  return `${units} ${unitLabel} en ${lots} ${lotLabel}`;
}

export function matchesOfficialUnitSearch(unit, query) {
  const term = String(query ?? '').trim().toLowerCase();
  if (!term) return true;
  const haystack = [
    getOfficialUnitNumber(unit),
    unit?.tax_account,
    unit?.valuation_period,
    formatValuationPeriod(unit?.valuation_period),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(term);
}
